import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError } from "../lib/api.ts";
import { initials } from "../lib/format.ts";
import { Icon } from "./Icon.tsx";

/** A screen: its heading sits in the page under the shared app bar, as on the website. Wide screens (the
 *  pipeline board) drop the right-hand column. `quiet` keeps the heading's place but hides it, for records whose
 *  own header (the record-head) already shows the name. */
export function Page({ title, back, actions, children, wide, quiet }: { title: ReactNode; back?: boolean | string; actions?: ReactNode; children: ReactNode; wide?: boolean; quiet?: boolean }) {
  const nav = useNavigate();
  useEffect(() => { if (typeof title === "string") document.title = `${title} · Grassick CRM`; }, [title]);
  useEffect(() => {
    document.body.classList.toggle("is-wide", !!wide);
    return () => document.body.classList.remove("is-wide");
  }, [wide]);
  return (
    <div className={"content" + (wide ? " wide" : "")}>
      <div className="pagehead">
        {back && (
          <button className="back" onClick={() => (typeof back === "string" ? nav(back) : nav(-1))} aria-label="Back">
            <Icon name="back" size={22} />
          </button>
        )}
        <h1 className={quiet ? "quiet" : undefined}>{title}</h1>
        {actions && <div className="appbar-actions">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function Section({ title, count, more, children }: { title: string; count?: number; more?: ReactNode; children: ReactNode }) {
  return (
    <section className="section">
      <div className="section-title"><span className="sq" />{title}{count != null && <span className="n">{count}</span>}{more && <span className="more">{more}</span>}</div>
      {children}
    </section>
  );
}

export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;
export const Loading = () => <div className="loading"><div className="spinner" /></div>;
export const Avatar = ({ name, blue, sm }: { name: string; blue?: boolean; sm?: boolean }) =>
  <div className={"avatar" + (blue ? " blue" : "") + (sm ? " sm" : "")} aria-hidden="true">{initials(name)}</div>;

export function ErrorView({ error }: { error: unknown }) {
  const e = error as ApiError;
  return <div className="banner danger"><span>{e?.status === 404 ? "Not found, or not visible to you." : e?.message ?? "Something went wrong."}</span></div>;
}

export function Sheet({ title, onClose, children, foot }: { title: string; onClose: () => void; children: ReactNode; foot?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    ref.current?.querySelector<HTMLElement>("input, textarea, select")?.focus();
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="sheet" role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <div className="grab" />
        <h2>{title}</h2>
        {children}
        {foot && <div className="foot">{foot}</div>}
      </div>
    </div>
  );
}

// ---- toasts ----
interface ToastMsg { id: number; text: string; err?: boolean }
const ToastCtx = createContext<(text: string, err?: boolean) => void>(() => {});
export const useToast = () => useContext(ToastCtx);
export function ToastProvider({ children }: { children: ReactNode }) {
  const [msg, setMsg] = useState<ToastMsg | null>(null);
  const show = useCallback((text: string, err?: boolean) => {
    const id = Date.now();
    setMsg({ id, text, err });
    setTimeout(() => setMsg((m) => (m?.id === id ? null : m)), err ? 6000 : 3500);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {msg && <div className={"toast" + (msg.err ? " err" : "")} role="status"><span className="sq" />{msg.text}</div>}
    </ToastCtx.Provider>
  );
}

/** Turns an API error into a toast; returns true if it was handled. */
export function useFail() {
  const toast = useToast();
  return (err: unknown) => toast(err instanceof ApiError ? err.message : "Something went wrong", true);
}

/** Opens a screen's add form when the phone's Add button sends ?add=1, then drops the flag from the address. */
export function useAddParam(open: () => void) {
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    if (sp.get("add") !== "1") return;
    open();
    const n = new URLSearchParams(sp); n.delete("add"); setSp(n, { replace: true });
  }, [sp]);
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}</label>;
}
